import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BackHeader, PageTemplate } from '@_widgets/common';
import { SettingGroup } from '@_widgets/settings';

import { termsApi, userApi } from '@_entities/user';

import { Bridge } from '@_shared/config';
import { useHTMLParse } from '@_shared/hooks';
import { RightChevronIcon } from '@_shared/icons';
import { toastGenerate } from '@_shared/libs';
import { SkeletonUI } from '@_shared/ui';
import { Typography } from '@_shared/ui/Typography';

import { getCurrentDateFormat } from './SettingPage.logic';
import * as S from './SettingPage.style';

type TOption1 = {
	title: string;
	link: string;
	label?: string | JSX.Element | JSX.Element[];
};
type TOption2 = {
	title: string;
	label?: string | JSX.Element | JSX.Element[];
	onClick: () => void;
};
type TOption3 = {
	title: string;
	onClick: () => void;
	isActive: boolean;
	label?: string | JSX.Element | JSX.Element[];
};

export function SettingPage() {
	const navigate = useNavigate();

	const [marketingTerms, setMarketingTerms] = useState<boolean>();
	const [nickname, setNickname] = useState<string>();

	useEffect(() => {
		(async () => {
			const { data: responseData } = await userApi.getUser();
			const { data } = await termsApi.getTerms();

			setNickname(responseData.nickname);
			setMarketingTerms(data[1].TERMS02);
		})();
	}, []);

	const SettingList: {
		name: string;
		options: (TOption1 | TOption2 | TOption3)[];
	}[] = [
		{
			name: '고객 지원',
			options: [
				{
					title: '공지사항',
					link: '/settings/announcement',
				},
				{
					title: '서비스 문의',
					link: 'http://pf.kakao.com/_xgxfxoxcG/chat',
				},
				{
					title: '의견 보내기',
					link: '/settings/feedback',
				},
			],
		},
		{
			name: '앱 설정',
			options: [
				{
					title: '폴더 순서 편집',
					onClick: () => {
						navigate('/folder-list/manual');
					},
				},
				{
					title: '앱 이용 정보 푸시 알림',
					onClick: () => {
						new Bridge().alertAccess();
					},
					label: useHTMLParse(
						'알림이 오지 않을 경우, 시스템 설정 &gt; 딸깍 앱의<br />알림 허용 여부를 확인해 주세요!',
					),
				},
				{
					title: '마케팅 정보 수신',
					onClick: () => {
						termsApi.postMarketingTerms();
						setMarketingTerms((prev) => {
							if (prev) {
								toastGenerate({
									message: `마케팅 정보 수신을 거부하셨습니다. (${getCurrentDateFormat()})`,
								});
							} else {
								toastGenerate({
									message: `마케팅 정보 수신을 동의하셨습니다. (${getCurrentDateFormat()})`,
								});
							}
							return !prev;
						});
					},
					isActive: true,
				},
			],
		},
		{
			name: '약관 및 정책',
			options: [
				{
					title: '서비스 이용약관',
					link: 'http://teamfore.notion.site/15c4abba6dbc4c788511d7466ca2a801?pvs=25',
				},
				{
					title: '개인정보 처리방침',
					link: 'https://teamfore.notion.site/61f78ba3a6924fe384b1ecb87bdab271?pvs=25',
				},
				{
					title: '마케팅 활용 및 정보 수신',
					link: 'https://teamfore.notion.site/e749ad0057564e09bb55ac73a9096f5b?pvs=4',
				},
				{
					title: '데이터 제공 정책',
					link: 'http://teamfore.notion.site/dbc5a006bf2a4a4d838ed9447d6731a0?pvs=25',
				},
				{
					title: '오픈소스 라이센스',
					link: 'https://teamfore.notion.site/815a648d0b5c40f3b2b262e146bfd0fc?pvs=25',
				},
			],
		},
	];

	return (
		<PageTemplate header={<BackHeader>설정</BackHeader>} nav={false}>
			{/* Profile Area */}
			<S.ProfileContainer
				onClick={() => {
					navigate('/user/edit');
				}}
			>
				{!nickname ? (
					<SkeletonUI width="200px" height="28px" borderRadius="8px" />
				) : (
					<Typography type="title-large" color="black-primary">
						{nickname}
					</Typography>
				)}
				<Typography type="body-large" color="gray-primary">
					프로필 및 계정
				</Typography>
				<S.ProfileShortCutIconWrapper>
					<RightChevronIcon />
				</S.ProfileShortCutIconWrapper>
			</S.ProfileContainer>

			<S.SettingGroupContainer>
				{SettingList.map((setting) => (
					<>
						<S.SeperateLine />
						<SettingGroup.Container key={`${setting.name} Group`}>
							<SettingGroup.SettingGroupHeader>
								{setting.name}
							</SettingGroup.SettingGroupHeader>
							<SettingGroup.SettingList>
								{setting.options.map((option) => {
									// Toggle 이 있는 Option UI
									if ('isActive' in option) {
										return (
											<>
												<SettingGroup.SettingItem
													key={`${option.title} Option`}
													isToggleUI
													isActive={marketingTerms}
													onClick={option.onClick}
												>
													{option.title}
												</SettingGroup.SettingItem>
											</>
										);
									}

									// 일반적인 Option UI
									return (
										<>
											<SettingGroup.SettingItem
												key={`${option.title} Option`}
												isToggleUI={false}
												onClick={() => {
													if ('link' in option) {
														if (option.link.startsWith('http')) {
															window.location.href = option.link;
														} else {
															navigate(option.link);
														}
													} else {
														option.onClick();
													}
												}}
											>
												{option.title}
											</SettingGroup.SettingItem>
											{/* Label Area */}
											{option.label && (
												<Typography
													type="label-medium"
													color="font-tertiary"
													textAlign="left"
												>
													{option.label}
												</Typography>
											)}
										</>
									);
								})}
							</SettingGroup.SettingList>
						</SettingGroup.Container>
					</>
				))}
			</S.SettingGroupContainer>
		</PageTemplate>
	);
}
